/**
 * Vendor Bot Shop - Product Detail Page
 * Compact design with media gallery (images, GIFs, videos)
 */

import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { BottomNavigation, QuantitySelector, PriceDisplay } from '../../components/customer';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

type MediaType = 'image' | 'gif' | 'video';

interface MediaItemFromAPI {
  url: string;
  type: MediaType;
}

interface ColorOption {
  id: number;
  name: string;
  hex: string | null;
  price_adjustment: number;
  is_absolute: boolean;
  stock: number | null;
}

interface SizeOption {
  id: number;
  name: string;
  price_adjustment: number;
  is_absolute: boolean;
  stock: number | null;
}

interface UnitOption {
  id: number;
  name: string;
  price: number;
  stock: number | null;
}

interface BulkTier {
  id: number;
  min_quantity: number;
  unit_price: number;
  label: string | null;
}

interface VariationConfig {
  colors_enabled: boolean;
  sizes_enabled: boolean;
  units_enabled: boolean;
  bulk_pricing_enabled: boolean;
}

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  compare_at_price: number | null;
  product_type: 'physical' | 'downloadable' | 'keys' | 'subscription';
  stock_quantity: number;
  track_inventory: boolean;
  allow_backorder: boolean;
  is_featured: boolean;
  requires_shipping: boolean;
  image_urls: string[];
  media_items?: MediaItemFromAPI[];
  tags: string[];
  category_name: string;
  category_id: number;
  digital_file_url?: string;
  // Variation data
  has_variations?: boolean;
  variation_config?: VariationConfig | null;
  colors?: ColorOption[];
  sizes?: SizeOption[];
  units?: UnitOption[];
  bulk_tiers?: BulkTier[];
}

interface ReviewStats {
  total_reviews: number;
  average_rating: string;
  five_star: number;
  four_star: number;
  three_star: number;
  two_star: number;
  one_star: number;
}

interface Review {
  id: number;
  username: string;
  rating: number;
  comment: string | null;
  images: string[];
  is_featured: boolean;
  admin_response: string | null;
  helpful_count: number;
  created_at: string;
}

interface MediaItem {
  url: string;
  type: MediaType;
}

const ProductDetail: React.FC = () => {
  const { botUsername, id } = useParams<{ botUsername: string; id: string }>();
  const navigate = useNavigate();
  const { token, isAuthenticated, login, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();

  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currency, setCurrency] = useState({ symbol: '$', code: 'USD' });
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const galleryRef = useRef<HTMLDivElement>(null);

  // Variation selections
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<number | null>(null);

  // Reviews state
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [totalReviewPages, setTotalReviewPages] = useState(1);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Fallback: Detect media type from URL if not provided by API
  const getMediaType = (url: string): MediaType => {
    const lower = url.toLowerCase();
    if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov') || lower.includes('type=video')) {
      return 'video';
    }
    if (lower.includes('.gif') || lower.includes('type=gif')) {
      return 'gif';
    }
    return 'image';
  };

  useEffect(() => {
    const loadProduct = async () => {
      setIsLoading(true);
      try {
        const productRes = await fetch(`${baseUrl}/api/shop/${botUsername}/products/${id}`);
        const productData = await productRes.json();
        if (productData.success) {
          setProduct(productData.data);
          // Use media_items from API if available (includes type), otherwise fallback to image_urls
          let items: MediaItem[];
          if (productData.data.media_items && productData.data.media_items.length > 0) {
            items = productData.data.media_items;
          } else {
            items = (productData.data.image_urls || []).map((url: string) => ({
              url,
              type: getMediaType(url)
            }));
          }
          setMediaItems(items);
        }

        const storeRes = await fetch(`${baseUrl}/api/shop/${botUsername}/store-info`);
        const storeData = await storeRes.json();
        if (storeData.success) {
          setCurrency({
            symbol: storeData.data.currency_symbol || '$',
            code: storeData.data.currency || 'USD'
          });
        }

        // Fetch reviews
        const reviewsRes = await fetch(`${baseUrl}/api/shop/${botUsername}/products/${id}/reviews?limit=5`);
        const reviewsData = await reviewsRes.json();
        if (reviewsData.success) {
          setReviewStats(reviewsData.data.stats);
          setReviews(reviewsData.data.reviews);
          setTotalReviewPages(reviewsData.data.pagination.total_pages);
        }
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [botUsername, id, baseUrl]);

  // Handle swipe for gallery
  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery || mediaItems.length <= 1) return;

    let startX = 0;
    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isDragging) return;
      isDragging = false;
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;

      if (Math.abs(diff) > 50) {
        if (diff > 0 && currentMediaIndex < mediaItems.length - 1) {
          setCurrentMediaIndex(prev => prev + 1);
        } else if (diff < 0 && currentMediaIndex > 0) {
          setCurrentMediaIndex(prev => prev - 1);
        }
        window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
      }
    };

    gallery.addEventListener('touchstart', handleTouchStart);
    gallery.addEventListener('touchend', handleTouchEnd);

    return () => {
      gallery.removeEventListener('touchstart', handleTouchStart);
      gallery.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentMediaIndex, mediaItems.length]);

  // Load more reviews for modal
  const loadMoreReviews = async (page: number) => {
    if (isLoadingReviews) return;
    setIsLoadingReviews(true);
    try {
      const res = await fetch(`${baseUrl}/api/shop/${botUsername}/products/${id}/reviews?page=${page}&limit=10`);
      const data = await res.json();
      if (data.success) {
        if (page === 1) {
          setReviews(data.data.reviews);
        } else {
          setReviews(prev => [...prev, ...data.data.reviews]);
        }
        setReviewsPage(page);
        setTotalReviewPages(data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  // Open reviews modal
  const handleOpenReviews = () => {
    if (reviewStats && reviewStats.total_reviews > 0) {
      setShowReviewsModal(true);
      loadMoreReviews(1);
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    }
  };

  // Render star rating
  const renderStars = (rating: number, size: number = 14) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<span key={i} style={{ color: '#FFB800', fontSize: size }}>★</span>);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<span key={i} style={{ color: '#FFB800', fontSize: size }}>★</span>);
      } else {
        stars.push(<span key={i} style={{ color: '#E0E0E0', fontSize: size }}>★</span>);
      }
    }
    return stars;
  };

  // Calculate price based on selected variations
  const calculatePrice = (): number => {
    if (!product) return 0;
    let price = product.price;

    // If unit is selected, use its price (absolute)
    if (selectedUnit && product.units) {
      const unit = product.units.find(u => u.id === selectedUnit);
      if (unit) {
        return unit.price;
      }
    }

    // Apply color adjustment
    if (selectedColor && product.colors) {
      const color = product.colors.find(c => c.id === selectedColor);
      if (color && !color.is_absolute) {
        price += color.price_adjustment;
      }
    }

    // Apply size adjustment
    if (selectedSize && product.sizes) {
      const size = product.sizes.find(s => s.id === selectedSize);
      if (size && !size.is_absolute) {
        price += size.price_adjustment;
      }
    }

    // Apply bulk pricing
    if (product.bulk_tiers && product.bulk_tiers.length > 0 && !selectedUnit) {
      const applicableTier = [...product.bulk_tiers]
        .sort((a, b) => b.min_quantity - a.min_quantity)
        .find(t => quantity >= t.min_quantity);
      if (applicableTier) {
        const basePrice = product.price;
        const bulkPrice = applicableTier.unit_price;
        const adjustment = price - basePrice;
        price = bulkPrice + adjustment;
      }
    }

    return price;
  };

  const currentPrice = calculatePrice();

  const handleAddToCart = async () => {
    if (!product) return;

    // Validate required variations
    const config = product.variation_config;
    if (config && product.product_type === 'physical') {
      if (config.colors_enabled && product.colors && product.colors.length > 0 && !selectedColor) {
        window.Telegram?.WebApp?.showAlert(t('variation.please_select_color'));
        return;
      }
      if (config.sizes_enabled && product.sizes && product.sizes.length > 0 && !selectedSize) {
        window.Telegram?.WebApp?.showAlert(t('variation.please_select_size'));
        return;
      }
      if (config.units_enabled && product.units && product.units.length > 0 && !selectedUnit) {
        window.Telegram?.WebApp?.showAlert(t('variation.please_select_option'));
        return;
      }
    }

    setIsAddingToCart(true);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');

    try {
      // Check if authenticated, try to login if not
      let authToken = token;
      if (!isAuthenticated || !authToken) {
        try {
          await login();
          authToken = localStorage.getItem('auth_token');
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          window.Telegram?.WebApp?.showAlert('Please login to add items to cart');
          setIsAddingToCart(false);
          return;
        }
      }

      if (!authToken) {
        window.Telegram?.WebApp?.showAlert('Authentication required. Please try again.');
        setIsAddingToCart(false);
        return;
      }

      const res = await fetch(`${baseUrl}/api/cart/${botUsername}/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: product.id,
          quantity: quantity,
          selectedColor: selectedColor,
          selectedSize: selectedSize,
          selectedUnit: selectedUnit
        })
      });

      const data = await res.json();
      if (data.success) {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        window.Telegram?.WebApp?.showAlert(t('common.addedToCart'));
      } else {
        window.Telegram?.WebApp?.showAlert(data.error || t('common.error'));
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      window.Telegram?.WebApp?.showAlert(t('common.error'));
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate(`/shop/${botUsername}/cart`);
  };

  const getStockStatus = () => {
    if (!product) return { text: '', color: '' };

    // For downloadable and subscription products, don't show quantity
    if (product.product_type === 'downloadable' || product.product_type === 'subscription') {
      if (!product.track_inventory || product.stock_quantity > 0) {
        return { text: t('shop.inStock'), color: '#4CAF50' };
      }
      return { text: t('shop.outOfStock'), color: '#f44336' };
    }

    // For physical and keys products, show quantity
    if (!product.track_inventory) {
      return { text: t('shop.inStock'), color: '#4CAF50' };
    }

    if (product.stock_quantity <= 0) {
      if (product.allow_backorder) {
        return { text: t('shop.backorder'), color: '#FFA000' };
      }
      return { text: t('shop.outOfStock'), color: '#f44336' };
    }

    // Show quantity for physical and keys products
    if (product.stock_quantity <= 5) {
      return { text: `${product.stock_quantity} ${t('shop.left')}`, color: '#FFA000' };
    }

    // Show "X in stock" for physical and keys products with more than 5 items
    return { text: `${product.stock_quantity} ${t('shop.inStock')}`, color: '#4CAF50' };
  };

  const isOutOfStock = product?.track_inventory &&
    product.stock_quantity <= 0 &&
    !product.allow_backorder;

  const maxQuantity = product?.track_inventory
    ? Math.min(product.stock_quantity, 99)
    : 99;

  // Render media item (image, gif, or video)
  const renderMedia = (item: MediaItem, isMain: boolean = false) => {
    if (item.type === 'video') {
      return (
        <Video
          src={item.url}
          controls
          playsInline
          preload="metadata"
          poster=""
        />
      );
    }
    return <MediaImage src={item.url} alt={product?.name || ''} />;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
        <BottomNavigation />
      </PageContainer>
    );
  }

  if (!product) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorText>{t('shop.productNotFound')}</ErrorText>
          <BackButton onClick={() => navigate(`/shop/${botUsername}`)}>
            {t('common.backToStore')}
          </BackButton>
        </ErrorContainer>
        <BottomNavigation />
      </PageContainer>
    );
  }

  const stockStatus = getStockStatus();

  return (
    <PageContainer>
      <Header>
        <BackBtn onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </BackBtn>
        <HeaderTitle>{product.name.substring(0, 20)}{product.name.length > 20 ? '...' : ''}</HeaderTitle>
        <CartBtn onClick={() => navigate(`/shop/${botUsername}/cart`)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </CartBtn>
      </Header>

      <MediaGallery ref={galleryRef}>
        {mediaItems.length > 0 ? (
          <>
            <MainMediaContainer>
              {renderMedia(mediaItems[currentMediaIndex], true)}
              {mediaItems.length > 1 && (
                <MediaCounter>{currentMediaIndex + 1}/{mediaItems.length}</MediaCounter>
              )}
              {mediaItems[currentMediaIndex].type === 'video' && (
                <MediaTypeBadge>VIDEO</MediaTypeBadge>
              )}
            </MainMediaContainer>
            {mediaItems.length > 1 && (
              <ThumbnailRow>
                {mediaItems.map((item, index) => (
                  <ThumbnailWrapper
                    key={index}
                    $active={index === currentMediaIndex}
                    onClick={() => setCurrentMediaIndex(index)}
                  >
                    {item.type === 'video' ? (
                      <ThumbnailVideo src={item.url} muted />
                    ) : (
                      <ThumbnailImg src={item.url} alt="" />
                    )}
                    {item.type === 'video' && <PlayIcon>▶</PlayIcon>}
                  </ThumbnailWrapper>
                ))}
              </ThumbnailRow>
            )}
          </>
        ) : (
          <PlaceholderImage>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
          </PlaceholderImage>
        )}
      </MediaGallery>

      <Content>
        <ProductCard>
          <TopRow>
            <TypeBadge $type={product.product_type}>
              {t(`shop.productTypes.${product.product_type}`)}
            </TypeBadge>
            <StockStatus $color={stockStatus.color}>
              {stockStatus.text}
            </StockStatus>
          </TopRow>

          <ProductName>{product.name}</ProductName>

          <PriceRow>
            <PriceDisplay
              price={currentPrice}
              originalPrice={currentPrice !== product.price ? product.price : product.compare_at_price}
              currencySymbol={currency.symbol}
              size="large"
            />
            {currentPrice !== product.price && (
              <PriceNote>
                {currentPrice < product.price ? t('variation.bulk_price') : t('variation.total')}
              </PriceNote>
            )}
          </PriceRow>

          {product.description && (
            <Description>{product.description}</Description>
          )}

          {product.category_name && (
            <CategoryTag>{product.category_name}</CategoryTag>
          )}

          {/* Reviews Section */}
          {reviewStats && (
            <ReviewsSection onClick={handleOpenReviews} $clickable={reviewStats.total_reviews > 0}>
              <ReviewsHeader>
                <ReviewsStars>
                  {renderStars(parseFloat(reviewStats.average_rating), 18)}
                  <ReviewsAverage>{reviewStats.average_rating}</ReviewsAverage>
                </ReviewsStars>
                <ReviewsCount>
                  {reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? t('reviews.review_singular') : t('reviews.reviews')}
                  {reviewStats.total_reviews > 0 && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 4 }}>
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                  )}
                </ReviewsCount>
              </ReviewsHeader>
            </ReviewsSection>
          )}
        </ProductCard>

        {/* Color Selection */}
        {product.colors && product.colors.length > 0 && product.variation_config?.colors_enabled && (
          <VariationSection>
            <VariationTitle>{t('variation.color')}</VariationTitle>
            <VariationOptions>
              {product.colors.map(color => (
                <ColorOption
                  key={color.id}
                  $selected={selectedColor === color.id}
                  $hex={color.hex}
                  onClick={() => {
                    setSelectedColor(selectedColor === color.id ? null : color.id);
                    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                  }}
                >
                  {color.hex && <ColorSwatch $hex={color.hex} />}
                  <span>{color.name}</span>
                  {color.price_adjustment > 0 && (
                    <PriceAdj>+{currency.symbol}{color.price_adjustment.toFixed(2)}</PriceAdj>
                  )}
                </ColorOption>
              ))}
            </VariationOptions>
          </VariationSection>
        )}

        {/* Size Selection */}
        {product.sizes && product.sizes.length > 0 && product.variation_config?.sizes_enabled && (
          <VariationSection>
            <VariationTitle>{t('variation.size')}</VariationTitle>
            <VariationOptions>
              {product.sizes.map(size => (
                <SizeOption
                  key={size.id}
                  $selected={selectedSize === size.id}
                  onClick={() => {
                    setSelectedSize(selectedSize === size.id ? null : size.id);
                    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                  }}
                >
                  <span>{size.name}</span>
                  {size.price_adjustment > 0 && (
                    <PriceAdj>+{currency.symbol}{size.price_adjustment.toFixed(2)}</PriceAdj>
                  )}
                </SizeOption>
              ))}
            </VariationOptions>
          </VariationSection>
        )}

        {/* Unit Selection */}
        {product.units && product.units.length > 0 && product.variation_config?.units_enabled && (
          <VariationSection>
            <VariationTitle>{t('variation.options')}</VariationTitle>
            <VariationOptions>
              {product.units.map(unit => (
                <UnitOption
                  key={unit.id}
                  $selected={selectedUnit === unit.id}
                  onClick={() => {
                    setSelectedUnit(selectedUnit === unit.id ? null : unit.id);
                    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                  }}
                >
                  <span>{unit.name}</span>
                  <UnitPrice>{currency.symbol}{unit.price.toFixed(2)}</UnitPrice>
                </UnitOption>
              ))}
            </VariationOptions>
          </VariationSection>
        )}

        {/* Bulk Pricing */}
        {product.bulk_tiers && product.bulk_tiers.length > 0 && product.variation_config?.bulk_pricing_enabled && !selectedUnit && (
          <VariationSection>
            <VariationTitle>{t('variation.bulk_deals')}</VariationTitle>
            <BulkTiers>
              {product.bulk_tiers.map(tier => {
                const discount = ((product.price - tier.unit_price) / product.price * 100).toFixed(0);
                const isActive = quantity >= tier.min_quantity;
                return (
                  <BulkTier
                    key={tier.id}
                    $active={isActive}
                    onClick={() => {
                      setQuantity(tier.min_quantity);
                      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
                    }}
                  >
                    <BulkQty>{tier.min_quantity}+</BulkQty>
                    <BulkPrice>{currency.symbol}{tier.unit_price.toFixed(2)}</BulkPrice>
                    <BulkDiscount>-{discount}%</BulkDiscount>
                  </BulkTier>
                );
              })}
            </BulkTiers>
          </VariationSection>
        )}

        {!isOutOfStock && !selectedUnit && (
          <QuantityRow>
            <QuantityLabel>{t('cart.quantity')}</QuantityLabel>
            <QuantitySelector
              quantity={quantity}
              onChange={setQuantity}
              min={1}
              max={maxQuantity}
              size="small"
            />
          </QuantityRow>
        )}
      </Content>

      <ActionBar>
        <AddToCartButton
          onClick={handleAddToCart}
          disabled={isOutOfStock || isAddingToCart}
        >
          {isAddingToCart ? (
            <SmallSpinner />
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-8.9-5h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L17.31 4l-3.86 7H8.53L4.27 2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2z"/>
              </svg>
              {t('common.add')}
            </>
          )}
        </AddToCartButton>
        <BuyNowButton
          onClick={handleBuyNow}
          disabled={isOutOfStock || isAddingToCart}
        >
          {t('common.buyNow')}
        </BuyNowButton>
      </ActionBar>

      <BottomNavigation />

      {/* Reviews Modal */}
      {showReviewsModal && (
        <ReviewsModal>
          <ReviewsModalOverlay onClick={() => setShowReviewsModal(false)} />
          <ReviewsModalContent>
            <ReviewsModalHeader>
              <ReviewsModalTitle>{t('reviews.product_reviews')}</ReviewsModalTitle>
              <ReviewsModalClose onClick={() => setShowReviewsModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </ReviewsModalClose>
            </ReviewsModalHeader>

            {reviewStats && (
              <ReviewsModalStats>
                <ReviewsModalAverage>
                  <span style={{ fontSize: 36, fontWeight: 700 }}>{reviewStats.average_rating}</span>
                  <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                    {renderStars(parseFloat(reviewStats.average_rating), 20)}
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--tg-theme-hint-color)', marginTop: 4 }}>
                    {reviewStats.total_reviews} {t('reviews.reviews')}
                  </span>
                </ReviewsModalAverage>
              </ReviewsModalStats>
            )}

            <ReviewsList>
              {reviews.length === 0 ? (
                <NoReviews>{t('reviews.no_reviews')}</NoReviews>
              ) : (
                reviews.map(review => (
                  <ReviewItem key={review.id}>
                    <ReviewItemHeader>
                      <ReviewAuthor>
                        <ReviewAvatar>{review.username.charAt(0).toUpperCase()}</ReviewAvatar>
                        <div>
                          <ReviewUsername>{review.username}</ReviewUsername>
                          <ReviewDate>
                            {new Date(review.created_at).toLocaleDateString()}
                          </ReviewDate>
                        </div>
                      </ReviewAuthor>
                      <ReviewRating>
                        {renderStars(review.rating, 14)}
                      </ReviewRating>
                    </ReviewItemHeader>
                    {review.comment && (
                      <ReviewComment>{review.comment}</ReviewComment>
                    )}
                    {review.admin_response && (
                      <AdminResponse>
                        <AdminResponseLabel>{t('reviews.vendor_response')}</AdminResponseLabel>
                        <AdminResponseText>{review.admin_response}</AdminResponseText>
                      </AdminResponse>
                    )}
                    {review.is_featured && (
                      <FeaturedBadge>⭐ {t('reviews.featured_review')}</FeaturedBadge>
                    )}
                  </ReviewItem>
                ))
              )}

              {reviewsPage < totalReviewPages && (
                <LoadMoreButton onClick={() => loadMoreReviews(reviewsPage + 1)} disabled={isLoadingReviews}>
                  {isLoadingReviews ? t('common.loading') : t('reviews.load_more')}
                </LoadMoreButton>
              )}
            </ReviewsList>
          </ReviewsModalContent>
        </ReviewsModal>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  padding-bottom: 110px;
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    padding-bottom: 120px;
  }

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--tg-theme-bg-color, #ffffff);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  position: sticky;
  top: 0;
  z-index: 100;

  @media (min-width: 375px) {
    padding: 8px 12px;
  }

  @media (min-width: 768px) {
    padding: 10px 16px;
  }
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--tg-theme-text-color, #000);
  cursor: pointer;
`;

const HeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CartBtn = styled(BackBtn)``;

const MediaGallery = styled.div`
  background: #000;
`;

const MainMediaContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 180px;
  max-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;

  @media (min-width: 375px) {
    min-height: 200px;
    max-height: 300px;
  }

  @media (min-width: 600px) {
    min-height: 250px;
    max-height: 400px;
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 260px;
  object-fit: contain;

  @media (min-width: 375px) {
    max-height: 300px;
  }

  @media (min-width: 600px) {
    max-height: 400px;
  }
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 260px;
  object-fit: contain;
  background: #000;

  @media (min-width: 375px) {
    max-height: 300px;
  }

  @media (min-width: 600px) {
    max-height: 400px;
  }
`;

const MediaCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.6);
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
`;

const MediaTypeBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0,0,0,0.7);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 3px;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
  color: var(--tg-theme-hint-color, #999);
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px 12px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThumbnailWrapper = styled.div<{ $active: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'transparent'};
  cursor: pointer;
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
`;

const Content = styled.main`
  padding: 8px;

  @media (min-width: 375px) {
    padding: 10px;
  }

  @media (min-width: 600px) {
    padding: 16px;
  }
`;

const ProductCard = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px;

  @media (min-width: 375px) {
    padding: 12px;
  }

  @media (min-width: 600px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF5020';
      case 'downloadable': return '#2196F320';
      case 'keys': return '#9C27B020';
      case 'subscription': return '#FF980020';
      default: return '#99999920';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'physical': return '#4CAF50';
      case 'downloadable': return '#2196F3';
      case 'keys': return '#9C27B0';
      case 'subscription': return '#FF9800';
      default: return '#999';
    }
  }};
`;

const StockStatus = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.$color};
`;

const ProductName = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
  margin: 0 0 6px 0;
  line-height: 1.3;

  @media (min-width: 375px) {
    font-size: 16px;
    margin: 0 0 8px 0;
  }

  @media (min-width: 600px) {
    font-size: 18px;
  }
`;

const PriceRow = styled.div`
  margin-bottom: 10px;
`;

const Description = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: var(--tg-theme-hint-color, #666);
  margin: 0 0 10px 0;
  white-space: pre-wrap;
`;

const CategoryTag = styled.span`
  display: inline-block;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
  color: var(--tg-theme-hint-color, #666);
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 10px;
`;

const PriceNote = styled.span`
  font-size: 10px;
  color: var(--tg-theme-hint-color, #999);
  margin-left: 6px;
`;

const VariationSection = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 8px;
`;

const VariationTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
  margin: 0 0 8px 0;
  text-transform: uppercase;
`;

const VariationOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ColorOption = styled.button<{ $selected: boolean; $hex: string | null }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #ddd)'};
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)10'
    : 'transparent'};
  color: var(--tg-theme-text-color, #000);

  &:active {
    transform: scale(0.97);
  }
`;

const ColorSwatch = styled.span<{ $hex: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.$hex};
  border: 1px solid rgba(0,0,0,0.15);
`;

const SizeOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #ddd)'};
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)10'
    : 'transparent'};
  color: var(--tg-theme-text-color, #000);

  &:active {
    transform: scale(0.97);
  }
`;

const UnitOption = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  min-width: 80px;
  border: 2px solid ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #ddd)'};
  background: ${props => props.$selected
    ? 'var(--tg-theme-button-color, #3390ec)10'
    : 'transparent'};
  color: var(--tg-theme-text-color, #000);

  &:active {
    transform: scale(0.97);
  }
`;

const UnitPrice = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--tg-theme-button-color, #3390ec);
  margin-top: 2px;
`;

const PriceAdj = styled.span`
  font-size: 10px;
  color: var(--tg-theme-hint-color, #999);
`;

const BulkTiers = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const BulkTier = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  min-width: 70px;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.15s;
  border: 2px solid ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)'
    : 'var(--tg-theme-hint-color, #ddd)'};
  background: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)10'
    : 'transparent'};

  &:active {
    transform: scale(0.97);
  }
`;

const BulkQty = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
`;

const BulkPrice = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--tg-theme-button-color, #3390ec);
`;

const BulkDiscount = styled.span`
  font-size: 10px;
  color: #4CAF50;
  font-weight: 500;
`;

const QuantityRow = styled.div`
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuantityLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000);
`;

const ActionBar = styled.div`
  position: fixed;
  bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  display: flex;
  gap: 6px;
  padding: 6px 10px;
  background: var(--tg-theme-bg-color, #ffffff);
  border-top: 1px solid rgba(0,0,0,0.06);
  z-index: 999;
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    gap: 8px;
    padding: 8px 12px;
  }

  @media (min-width: 768px) {
    max-width: 900px;
    padding: 10px 16px;
  }
`;

const AddToCartButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1.5px solid var(--tg-theme-button-color, #3390ec);
  background: transparent;
  color: var(--tg-theme-button-color, #3390ec);

  svg {
    width: 12px;
    height: 12px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  @media (min-width: 375px) {
    gap: 4px;
    padding: 8px 12px;
    font-size: 13px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (min-width: 600px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const BuyNowButton = styled.button`
  flex: 1;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  @media (min-width: 375px) {
    padding: 8px 12px;
    font-size: 13px;
  }

  @media (min-width: 600px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

const SmallSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--tg-theme-hint-color, #e0e0e0);
  border-top-color: var(--tg-theme-button-color, #3390ec);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 20px;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: var(--tg-theme-hint-color, #999);
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #ffffff);
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

// Reviews Styled Components
const ReviewsSection = styled.div<{ $clickable: boolean }>`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
`;

const ReviewsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ReviewsStars = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReviewsAverage = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
  margin-left: 8px;
`;

const ReviewsCount = styled.span`
  font-size: 14px;
  color: var(--tg-theme-link-color, #3390ec);
  display: flex;
  align-items: center;
`;

const ReviewsBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RatingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RatingLabel = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  width: 28px;
`;

const RatingBarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: var(--tg-theme-hint-color, #e0e0e0)30;
  border-radius: 3px;
  overflow: hidden;
`;

const RatingBarFill = styled.div`
  height: 100%;
  background: #FFB800;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const RatingCount = styled.span`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
  width: 24px;
  text-align: right;
`;

// Reviews Modal Components
const ReviewsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ReviewsModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const ReviewsModalContent = styled.div`
  position: relative;
  margin-top: auto;
  background: var(--tg-theme-bg-color, #ffffff);
  border-radius: 16px 16px 0 0;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ReviewsModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;
`;

const ReviewsModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000);
  margin: 0;
`;

const ReviewsModalClose = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--tg-theme-hint-color, #999);
`;

const ReviewsModalStats = styled.div`
  padding: 16px;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;
`;

const ReviewsModalAverage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--tg-theme-text-color, #000);
`;

const ReviewsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const NoReviews = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--tg-theme-hint-color, #999);
  font-size: 14px;
`;

const ReviewItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid var(--tg-theme-hint-color, #e0e0e0)20;

  &:last-child {
    border-bottom: none;
  }
`;

const ReviewItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ReviewAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReviewAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--tg-theme-button-color, #3390ec);
  color: var(--tg-theme-button-text-color, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const ReviewUsername = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--tg-theme-text-color, #000);
`;

const ReviewDate = styled.div`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #999);
`;

const ReviewRating = styled.div`
  display: flex;
  gap: 1px;
`;

const ReviewComment = styled.p`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000);
  line-height: 1.5;
  margin: 0;
`;

const AdminResponse = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border-radius: 8px;
`;

const AdminResponseLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--tg-theme-link-color, #3390ec);
  margin-bottom: 4px;
`;

const AdminResponseText = styled.p`
  font-size: 13px;
  color: var(--tg-theme-text-color, #000);
  margin: 0;
  line-height: 1.4;
`;

const FeaturedBadge = styled.div`
  display: inline-block;
  margin-top: 8px;
  padding: 4px 8px;
  background: #FFF3CD;
  color: #856404;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 16px;
  background: var(--tg-theme-secondary-bg-color, #f5f5f5);
  border: none;
  border-radius: 8px;
  color: var(--tg-theme-link-color, #3390ec);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ProductDetail;
